import { money } from "../../Subscription/money";

/**
 * The CRM offer editor's arithmetic and words — pure, so it is tested. It mirrors
 * the backend's pricing (usage.services.pricing), which is what actually charges:
 * the preview here is a courtesy, and the server re-prices and refuses anything
 * below the $1 floor.
 *
 * Plans arrive as tier rows: `price` is the MONTHLY rate; a 12-month plan
 * (`term_months` 12, or `cadence` "annual" on an offer's tiers) is charged
 * price × 12 once a year.
 */

export const MIN_CHARGE = 1;

const toCents = (n) => Math.round(Number(n || 0) * 100);
const fromCents = (c) => (c / 100).toFixed(2);

export const isAnnual = (tier) =>
  !!tier && (tier.cadence === "annual" || Number(tier.term_months) >= 12);

export const familyOf = (tier) =>
  (tier && tier.family) || String((tier && tier.title) || "").replace(/\s*\((Annual|Monthly)\)\s*$/, "").trim();

/** Plans grouped for the matrix: [{family, annual, monthly}], cheapest first. */
export function groupFamilies(tiers) {
  const byName = new Map();
  (tiers || []).forEach((t) => {
    const name = familyOf(t);
    if (!byName.has(name)) byName.set(name, { family: name, annual: null, monthly: null, minPrice: Infinity });
    const row = byName.get(name);
    if (isAnnual(t)) row.annual = t;
    else row.monthly = t;
    row.minPrice = Math.min(row.minPrice, Number(t.price));
  });
  return Array.from(byName.values()).sort((a, b) => a.minPrice - b.minPrice);
}

/** One charge before tax, in cents: the year for a 12-month plan. */
function listChargeCents(tier) {
  return isAnnual(tier) ? toCents(tier.price) * 12 : toCents(tier && tier.price);
}

/**
 * What a discount does to one plan's charge (money as "0.00" strings).
 * `type`: "amount" comes off the MONTHLY rate, so ×12 on a 12-month plan;
 * "percent" comes off the whole charge.
 */
export function previewDiscount(tier, type, value) {
  const list = listChargeCents(tier);
  const v = Number(value || 0);
  let discount = 0;
  if (v > 0) {
    discount = type === "percent"
      ? Math.round((list * v) / 100)
      : toCents(v) * (isAnnual(tier) ? 12 : 1);
  }
  const charge = list - discount;
  return {
    listCharge: fromCents(list),
    discount: fromCents(Math.max(0, Math.min(discount, list))),
    charge: fromCents(Math.max(0, charge)),
    belowFloor: v > 0 && charge < toCents(MIN_CHARGE),
    perYear: isAnnual(tier),
  };
}

/** "$50 off the monthly rate" / "20% off" / "Special plan: Pro Plus". */
export function describeOffer(offer) {
  if (!offer) return "";
  if (offer.kind === "plan") {
    const names = Array.from(new Set((offer.tiers || []).map(familyOf)));
    return names.length ? `Special plan: ${names.join(", ")}` : "Special plan";
  }
  const v = Number(offer.discount_value || 0);
  return offer.discount_type === "percent" ? `${money(v)}% off` : `$${money(v)} off the monthly rate`;
}

/** The plans an offer covers, briefly: "Starter · Pro (12-month only)". */
export function describeOfferPlans(offer) {
  if (!offer) return "";
  return groupFamilies(offer.tiers || [])
    .map((f) => {
      if (f.annual && f.monthly) return f.family;
      return `${f.family} (${f.annual ? "12-month" : "monthly"} only)`;
    })
    .join(" · ");
}

export const OFFER_STATUS = {
  open: { color: "green", label: "Open" },
  expired: { color: "default", label: "Expired" },
  revoked: { color: "red", label: "Revoked" },
};

/** "Claim by Oct 31, 2026" / "No end date" — the last day to claim. */
export function claimByText(isoDate) {
  if (!isoDate) return "No end date";
  const [y, m, d] = String(isoDate).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return "No end date";
  const date = new Date(y, m - 1, d);
  return `Claim by ${date.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })}`;
}

/**
 * Special plans (the templates) grouped the way they were made: one group per
 * create call — the Monthly and Annual rows share a name and were saved together.
 */
export function templateGroups(plans) {
  const groups = new Map();
  (plans || []).forEach((p) => {
    const minute = String(p.created_at || "").slice(0, 16);
    const key = `${familyOf(p)}|${(p.based_on && p.based_on.id) || ""}|${minute}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key, family: familyOf(p), basedOn: p.based_on || null, createdAt: p.created_at || "",
        createdBy: p.created_by || null, annual: null, monthly: null, tiers: [],
      });
    }
    const g = groups.get(key);
    g.tiers.push(p);
    if (isAnnual(p)) g.annual = p;
    else g.monthly = p;
  });
  return Array.from(groups.values())
    .map((g) => {
      const seen = new Set();
      const offers = [];
      g.tiers.forEach((t) => (t.offers || []).forEach((o) => {
        if (!seen.has(o.id)) { seen.add(o.id); offers.push(o); }
      }));
      return {
        ...g,
        offers,
        purchases: g.tiers.reduce((n, t) => n + Number(t.purchases || 0), 0),
        editable: g.tiers.every((t) => t.editable !== false),
      };
    })
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0));
}

/** Where a template has been offered: "Coventry Dental, Wellesley Dental Group". */
export function offeredToText(group) {
  const names = Array.from(new Set(
    (group.offers || []).map((o) => o.office_name || o.practice_name).filter(Boolean),
  ));
  if (!names.length) return "Not offered yet";
  return `Offered to ${names.join(", ")}`;
}

/** Why the offer can't be saved yet, or null. */
export function offerFormError(form) {
  if (!form) return "Fill in the offer.";
  const tierIds = form.tierIds || [];
  if (form.kind === "discount") {
    const v = Number(form.discountValue);
    if (!(v > 0)) return "Enter the discount.";
    if (form.discountType === "percent" && v >= 100) {
      return "A percentage must be under 100%. For free access use Complimentary access.";
    }
    if (!tierIds.length) return "Choose at least one plan.";
    const low = (form.tiers || []).filter((t) => tierIds.includes(t.id))
      .find((t) => previewDiscount(t, form.discountType, v).belowFloor);
    if (low) return `That brings ${low.title} below $1. For free access use Complimentary access.`;
    return null;
  }
  if (!tierIds.length) return "Choose or create the special plan to offer.";
  return null;
}

/** The POST /crm/offers/ body. `scope` is {officeId} or {practiceId}. */
export function offerPayload(form, scope) {
  const body = {
    kind: form.kind,
    tier_ids: form.tierIds || [],
    label: (form.label || "").trim(),
    expires_on: form.expiresOn || null,
  };
  if (scope && scope.officeId) body.office_id = scope.officeId;
  else if (scope && scope.practiceId) body.practice_id = scope.practiceId;
  if (form.kind === "discount") {
    body.discount_type = form.discountType;
    body.discount_value = String(form.discountValue);
  }
  return body;
}

/** Why the special plan can't be saved yet, or null. */
export function customPlanError(plan) {
  if (!plan || !plan.baseTierId) return "Choose the plan to start from.";
  if (!(plan.name || "").trim()) return "Give the plan a name.";
  const cadences = ["monthly", "annual"].filter((c) => plan[c] && plan[c].on);
  if (!cadences.length) return "Choose monthly, 12-month, or both.";
  const bad = cadences.find((c) => !(Number(plan[c].price) >= MIN_CHARGE));
  if (bad) return `Enter a ${bad === "annual" ? "12-month" : "monthly"} price of at least $1 a month.`;
  return null;
}

/** The POST /crm/custom-plans/ body. Blank FTC caps mean unlimited. */
export function customPlanPayload(plan) {
  const variants = {};
  ["monthly", "annual"].forEach((c) => {
    if (plan[c] && plan[c].on) variants[c] = { price: String(plan[c].price) };
  });
  const cap = (v) => (v === null || v === undefined || v === "" ? null : Number(v));
  return {
    base_tier_id: plan.baseTierId,
    name: (plan.name || "").trim(),
    variants,
    descriptions: (plan.descriptions || []).map((d) => (d || "").trim()),
    limit_ftc_daily: cap(plan.ftcDaily),
    limit_ftc_monthly: cap(plan.ftcMonthly),
  };
}
